/**
 * HaulerCentral Auth Provider
 *
 * Context provider for HaulerCentral authentication.
 * Following patterns from @commander/auth.
 */

import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { AUTH_STORAGE_KEYS } from '../constants/authConstants';
import { useHaulerCentralAuth } from '../hooks/useHaulerCentralAuth';
import * as tokenProvider from '../lib/tokenProvider';
import type { HaulerCentralUser } from '../types/auth.types';

export interface HaulerCentralAuthState {
  user: HaulerCentralUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface HaulerCentralAuthContextValue extends HaulerCentralAuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (data: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<HaulerCentralUser>) => void;
  clearError: () => void;
  refreshToken: () => Promise<boolean>;
}

const HaulerCentralAuthContext = createContext<HaulerCentralAuthContextValue | undefined>(
  undefined
);

interface HaulerCentralAuthProviderProps {
  children: React.ReactNode;
  /**
   * Enable automatic token refresh
   * @default true
   */
  enableAutoRefresh?: boolean;
  /**
   * Token refresh interval in milliseconds
   * @default 300000 (5 minutes)
   */
  refreshInterval?: number;
}

export function HaulerCentralAuthProvider({
  children,
  enableAutoRefresh = true,
  refreshInterval = 5 * 60 * 1000, // 5 minutes
}: HaulerCentralAuthProviderProps) {
  const auth = useHaulerCentralAuth();
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize auth state from storage on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if we have tokens in localStorage
        const token = tokenProvider.getAccessToken();
        const userStr = localStorage.getItem(AUTH_STORAGE_KEYS.USER);

        if (token && userStr) {
          try {
            JSON.parse(userStr) as HaulerCentralUser;
            // If we have a token but auth state doesn't reflect it, update it
            if (!auth.isAuthenticated && !auth.user) {
              // Auth will be set by the useHaulerCentralAuth hook
            }
          } catch (error) {
            console.error('[HaulerCentralAuthProvider] Failed to parse stored user:', error);
          }
        }
      } catch (error) {
        console.error('[HaulerCentralAuthProvider] Failed to initialize auth:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    initAuth();
  }, [auth.isAuthenticated, auth.user]);

  // Auto-refresh token periodically
  useEffect(() => {
    if (!enableAutoRefresh || !auth.isAuthenticated) {
      return;
    }

    const interval = setInterval(async () => {
      const token = tokenProvider.getAccessToken();
      if (token && tokenProvider.isTokenExpired(token)) {
        await auth.refreshToken();
      }
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [enableAutoRefresh, refreshInterval, auth.isAuthenticated, auth.refreshToken]);

  // Listen for auth events from other tabs/windows
  useEffect(() => {
    const handleLogin = () => {
      // Auth state will be updated by useHaulerCentralAuth hook
    };

    const handleLogout = () => {
      // Auth state will be updated by useHaulerCentralAuth hook
    };

    window.addEventListener('auth:login', handleLogin);
    window.addEventListener('auth:logout', handleLogout);

    return () => {
      window.removeEventListener('auth:login', handleLogin);
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, []);

  const login = async (email: string, password: string) => {
    await auth.login({ email, password });
  };

  const register = async (data: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }) => {
    await auth.register(data);
  };

  const logout = async () => {
    await auth.logout();
  };

  const updateUser = (userData: Partial<HaulerCentralUser>) => {
    auth.updateUser(userData);
  };

  const clearError = () => {
    auth.clearError();
  };

  const contextValue: HaulerCentralAuthContextValue = {
    user: auth.user,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading || !isInitialized,
    error: auth.error,
    login,
    register,
    logout,
    updateUser,
    clearError,
    refreshToken: auth.refreshToken,
  };

  return (
    <HaulerCentralAuthContext.Provider value={contextValue}>
      {children}
    </HaulerCentralAuthContext.Provider>
  );
}

/**
 * Hook to access HaulerCentral auth context
 */
export function useHaulerCentralAuthContext(): HaulerCentralAuthContextValue {
  const context = useContext(HaulerCentralAuthContext);

  if (context === undefined) {
    throw new Error('useHaulerCentralAuthContext must be used within a HaulerCentralAuthProvider');
  }

  return context;
}

/**
 * Note: Use useHaulerCentralAuthContext() within HaulerCentralAuthProvider,
 * or useHaulerCentralAuth() to use the global auth store directly.
 */

export default HaulerCentralAuthProvider;
